import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAgent, useDeleteAgent, useActivateAgent, useDeactivateAgent, useCloneAgent } from '../hooks/useAgents';
import { AgentStatus, AgentType } from '../types/agent';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { DeleteConfirmModal, CloneAgentModal, RunAgentModal, VersionHistory, PublishTemplateModal } from '../components/agent';
import type { PublishTemplateFields } from '../components/agent';
import { usePublishTemplate } from '../hooks/useTemplates';
import { AgentDetailSkeleton } from '../components/common/LoadingSkeleton';
import { JsonEditor } from '../components/ui/json-editor';
import {
  useAgentExecutions,
  getExecutionStatusColor,
  getExecutionStatusIcon,
  formatDuration,
} from '../hooks/useExecution';

export default function AgentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);

  const { data: agent, isLoading, error } = useAgent(id);
  const deleteAgent = useDeleteAgent();
  const activateAgent = useActivateAgent();
  const deactivateAgent = useDeactivateAgent();
  const cloneAgent = useCloneAgent();
  const publishTemplate = usePublishTemplate();

  const handlePublish = (fields: PublishTemplateFields) => {
    if (!agent) return;
    publishTemplate.mutate(
      {
        ...fields,
        type: agent.type,
        configuration: (agent.config ?? {}) as Record<string, unknown>,
      },
      { onSuccess: () => setShowPublishModal(false) }
    );
  };

  const handleDelete = async () => {
    if (!id) return;
    await deleteAgent.mutateAsync(id);
    navigate('/agents');
  };

  const handleToggleStatus = async () => {
    if (!agent || !id) return;
    if (agent.status === 'ACTIVE') {
      await deactivateAgent.mutateAsync(id);
    } else {
      await activateAgent.mutateAsync(id);
    }
  };

  const handleClone = async (newName: string) => {
    if (!id) return;
    const clonedAgent = await cloneAgent.mutateAsync({ id, name: newName });
    setShowCloneModal(false);
    // Navigate to the newly cloned agent's detail page
    navigate(`/agents/${clonedAgent.id}`);
  };

  const getStatusBadgeClass = (status: AgentStatus) => {
    const baseClass = 'px-3 py-1 text-sm rounded-full font-medium';
    switch (status) {
      case 'ACTIVE':
        return `${baseClass} bg-green-100 text-green-800`;
      case 'INACTIVE':
        return `${baseClass} bg-gray-100 text-gray-800`;
      case 'PAUSED':
        return `${baseClass} bg-yellow-100 text-yellow-800`;
      case 'ERROR':
        return `${baseClass} bg-red-100 text-red-800`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800`;
    }
  };

  const getTypeIcon = (type: AgentType) => {
    switch (type) {
      case 'CUSTOMER_SUPPORT':
        return '💬';
      case 'DATA_PROCESSING':
        return '📊';
      case 'WORKFLOW_AUTOMATION':
        return '⚙️';
      case 'CONTENT_GENERATION':
        return '✍️';
      case 'ANALYSIS':
        return '🔍';
      case 'INTEGRATION':
        return '🔌';
      case 'CUSTOM':
        return '🔧';
      default:
        return '🤖';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-4xl mx-auto py-8">
          <AgentDetailSkeleton />
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-4xl mx-auto py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-6xl mb-4">❌</div>
              <CardTitle className="mb-2">Agent Not Found</CardTitle>
              <CardDescription className="mb-4">
                The agent you're looking for doesn't exist or has been deleted.
              </CardDescription>
              <Button onClick={() => navigate('/agents')}>
                Back to Agents
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-4xl mx-auto py-8">
        {/* Back Link */}
        <Link
          to="/agents"
          className="text-sm text-primary hover:underline mb-4 inline-block"
        >
          ← Back to Agents
        </Link>

        {/* Header Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="text-5xl">{getTypeIcon(agent.type)}</div>
                <div>
                  <CardTitle className="text-2xl mb-2">{agent.name}</CardTitle>
                  <CardDescription className="text-base">
                    {agent.description || 'No description provided'}
                  </CardDescription>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className={getStatusBadgeClass(agent.status)}>
                      {agent.status}
                    </span>
                    <span className="px-3 py-1 text-sm rounded-full font-medium bg-blue-100 text-blue-800">
                      {agent.type.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowRunModal(true)}
                  disabled={agent.status !== 'ACTIVE'}
                  title={agent.status !== 'ACTIVE' ? 'Agent must be active to run' : 'Run agent'}
                >
                  Run
                </Button>
                <Button
                  variant="outline"
                  onClick={handleToggleStatus}
                  disabled={activateAgent.isPending || deactivateAgent.isPending}
                >
                  {agent.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/agents/${id}/edit`)}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCloneModal(true)}
                  disabled={cloneAgent.isPending}
                >
                  Clone
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowPublishModal(true)}
                  title="Share this agent as a marketplace template"
                >
                  Publish
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Info Cards */}
        <div className="grid gap-4 md:grid-cols-3 mb-6">
          <Card
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => document.getElementById('version-history')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Version
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{agent.version}</p>
              <p className="text-xs text-muted-foreground">Click to view history</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Created
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm font-medium">{formatDate(agent.createdAt)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Last Updated
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm font-medium">{formatDate(agent.updatedAt)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Configuration */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Configuration</CardTitle>
            <CardDescription>
              Agent configuration and settings
            </CardDescription>
          </CardHeader>
          <CardContent>
            {agent.config && Object.keys(agent.config).length > 0 ? (
              <JsonEditor
                value={JSON.stringify(agent.config, null, 2)}
                onChange={() => {}}
                disabled
                height={200}
              />
            ) : (
              <p className="text-muted-foreground text-sm">
                No configuration set for this agent.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Version History */}
        <div className="mb-6">
          <VersionHistory
            agentId={agent.id}
            agentName={agent.name}
            currentVersion={agent.version}
          />
        </div>

        {/* Recent Executions */}
        <RecentExecutions agentId={agent.id} />
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        agentName={agent.name}
        isDeleting={deleteAgent.isPending}
      />

      {/* Clone Agent Modal */}
      <CloneAgentModal
        isOpen={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        onConfirm={handleClone}
        agentName={agent.name}
        isCloning={cloneAgent.isPending}
      />

      {/* Run Agent Modal */}
      <RunAgentModal
        isOpen={showRunModal}
        onClose={() => setShowRunModal(false)}
        agentId={agent.id}
        agentName={agent.name}
      />

      {/* Publish as Template Modal */}
      <PublishTemplateModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={handlePublish}
        agent={agent}
        isSubmitting={publishTemplate.isPending}
      />
    </div>
  );
}

/**
 * Recent Executions Component
 */
function RecentExecutions({ agentId }: { agentId: string }) {
  const { data, isLoading } = useAgentExecutions(agentId, { limit: 5 });
  const executions = data?.data || [];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Recent Executions</CardTitle>
            <CardDescription>
              Latest execution history for this agent
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/executions?agentId=${agentId}`}>View All</Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-12 bg-slate-100 rounded animate-pulse"
              />
            ))}
          </div>
        ) : executions.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-8">
            No executions yet. Run the agent to see execution history.
          </p>
        ) : (
          <div className="space-y-2">
            {executions.map((execution) => (
              <Link
                key={execution.id}
                to={`/executions/${execution.id}`}
                className="block"
              >
                <div className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">
                      {getExecutionStatusIcon(execution.status)}
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full font-medium ${getExecutionStatusColor(
                            execution.status
                          )}`}
                        >
                          {execution.status}
                        </span>
                        <span className="text-xs text-muted-foreground font-mono">
                          {execution.id.slice(0, 8)}...
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatDate(execution.startedAt || execution.createdAt)}
                        {execution.duration !== null && (
                          <span className="ml-2">
                            ({formatDuration(execution.duration)})
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm text-primary">View →</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
